/**
 * GrantElective — teacher/admin tool to grant an elective course to any student.
 * Mirrors the shape of GrantScholarDollars. Adds course_id to student.elective_purchases
 * (idempotent via backend $addToSet), then reports success + writes an audit log entry.
 */
import React, { useEffect, useState } from 'react';
import { GraduationCap, Loader2, Search, Sparkles } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const GrantElective = () => {
  const [students, setStudents] = useState([]);
  const [electives, setElectives] = useState([]);
  const [q, setQ] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, e] = await Promise.all([
          api.get('/academy/admin/students-lite', { headers: authHeaders() }),
          api.get('/academy/admin/electives-catalog', { headers: authHeaders() }),
        ]);
        setStudents(s.data.students || []);
        // Some options live outside `ELECTIVES_CATALOG` because they have
        // their own tier / access-flag system. We inject them as synthetic
        // dropdown entries and route the submit to the right endpoint.
        const catalog = e.data.electives || [];
        const synthetic = [
          {
            id: 'lchaim_guardian',
            title: "L'Chaim · Guardian (30 Days)",
            tagline: "Comped Guardian-tier access to the L'Chaim 30-day program.",
            duration_weeks: 4,
            credit_type: 'Enrichment Elective',
            credit_amount: 0.5,
            _kind: 'lchaim',
          },
          {
            id: 'basketball_iq_comp',
            title: 'IQ · Basketball IQ Full Access',
            tagline: 'Full 24-week Sport IQ curriculum, comped.',
            duration_weeks: 24,
            credit_type: 'Sport IQ Elective',
            credit_amount: 1.0,
            _kind: 'sport_iq',
          },
          {
            id: 'apex_course_comp',
            title: 'APEX · Founder Course (Full Access)',
            tagline: 'Unlock all four APEX modules — the sovereign-athlete course.',
            duration_weeks: 8,
            credit_type: 'Sovereign Elective',
            credit_amount: 1.0,
            _kind: 'apex',
          },
          {
            id: 'fuel_program_comp',
            title: 'Fuel · Nutrition Program',
            tagline: 'Comped access to the Fuel nutrition & recovery track.',
            duration_weeks: 12,
            credit_type: 'Wellness Elective',
            credit_amount: 0.5,
            _kind: 'fuel',
          },
        ];
        setElectives([...synthetic, ...catalog]);
      } catch (err) {
        // Silent — teachers without full admin may not have access to students-lite
      }
    })();
  }, []);

  const filtered = students.filter(
    (s) => !q || (s.name || '').toLowerCase().includes(q.toLowerCase()) || (s.email || '').toLowerCase().includes(q.toLowerCase())
  );
  const student = students.find((s) => s.id === selectedStudent);
  const course = electives.find((e) => e.id === selectedCourse);

  const grant = async () => {
    if (!selectedStudent) { toast.error('Pick a student first'); return; }
    if (!selectedCourse) { toast.error('Pick an elective to grant'); return; }
    const selectedObj = electives.find((e) => e.id === selectedCourse);
    setBusy(true);
    try {
      let r;
      const email = students.find((s) => s.id === selectedStudent)?.email;
      if (selectedObj?._kind === 'lchaim') {
        if (!email) { toast.error('Selected student has no email on file'); return; }
        r = await api.post('/academy/admin/grant-lchaim',
          { email, tier: 'lchaim_guardian' },
          { headers: authHeaders() });
      } else if (selectedObj?._kind === 'sport_iq' || selectedObj?._kind === 'apex' || selectedObj?._kind === 'fuel') {
        // Comp these gated programs via the shared access-flag admin endpoint.
        if (!email) { toast.error('Selected student has no email on file'); return; }
        r = await api.post('/academy/admin/grant-access',
          { email, kind: selectedObj._kind, reason },
          { headers: authHeaders() });
      } else {
        r = await api.post('/academy/admin/grant-elective',
          { student_id: selectedStudent, course_id: selectedCourse, reason },
          { headers: authHeaders() });
      }
      toast.success(r.data.message || 'Access granted');
      setReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not grant access');
    } finally { setBusy(false); }
  };

  return (
    <div className="border-2 border-purple-500/30 bg-purple-500/[0.03] p-5" data-testid="grant-elective-panel">
      <div className="flex items-center gap-2 mb-4">
        <GraduationCap className="w-4 h-4 text-purple-400" />
        <span className="text-xs font-black tracking-[0.2em] uppercase text-purple-400">Grant Elective Access</span>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Student</label>
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 text-zinc-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name or email…"
              className="w-full bg-black border-2 border-zinc-800 pl-8 pr-3 py-2 text-sm text-white outline-none focus:border-purple-500"
              data-testid="grant-elective-search"
            />
          </div>
          <select
            value={selectedStudent}
            onChange={(e) => setSelectedStudent(e.target.value)}
            className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-purple-500"
            data-testid="grant-elective-student-select"
          >
            <option value="">Select a student…</option>
            {filtered.map((s) => (
              <option key={s.id} value={s.id}>{s.name} · G{s.grade_level ?? '?'}</option>
            ))}
          </select>
          {student && (
            <div className="mt-2 text-[11px] text-zinc-400">Selected: <span className="text-purple-300 font-black">{student.name}</span></div>
          )}
        </div>

        <div>
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1">Elective</label>
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-purple-500 mb-2"
            data-testid="grant-elective-course-select"
          >
            <option value="">Select an elective…</option>
            {electives.map((e) => (
              <option key={e.id} value={e.id}>
                {e.title} · {e.duration_weeks}w · {e.credit_amount ? `${e.credit_amount} ${e.credit_type}` : e.credit_type}
              </option>
            ))}
          </select>
          {course && (
            <div className="text-[11px] text-zinc-400 border border-zinc-800 bg-black/60 px-3 py-2">
              <div className="text-purple-300 font-bold">{course.title}</div>
              <div className="text-zinc-500 mt-0.5 italic">{course.tagline}</div>
              <div className="mt-1 text-[10px] text-zinc-500">{course.credit_amount} {course.credit_type} · {course.duration_weeks} weeks</div>
            </div>
          )}
          <label className="block text-[9px] uppercase tracking-widest text-zinc-500 font-black mb-1 mt-3">Reason (optional)</label>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Scholarship winner, teacher pilot cohort"
            className="w-full bg-black border-2 border-zinc-800 px-3 py-2 text-sm text-white outline-none focus:border-purple-500"
            data-testid="grant-elective-reason"
          />
        </div>
      </div>

      <button
        onClick={grant}
        disabled={busy || !selectedStudent || !selectedCourse}
        className="mt-4 flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black px-5 py-2.5 text-[11px] font-black tracking-[0.2em] uppercase transition-colors"
        data-testid="grant-elective-submit-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4" /> Grant Elective Access</>}
      </button>
    </div>
  );
};

export default GrantElective;
